/**
 * Banking Partner Dashboard
 * Portal for banking partners (Sterling Bank, Scotiabank) to view leads
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Building2,
  Users,
  DollarSign,
  FileText,
  Download,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  Search,
  RefreshCw,
  LogOut,
  TrendingUp,
  Landmark,
  Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const BankingPartnerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({
    total_leads: 0,
    approved: 0,
    pending: 0,
    total_loan_value: 0,
    total_commission_owed: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.get(`${API}/api/banking-partner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeads(response.data.leads || []);
      setStats(response.data.stats || {});
      setPartnerInfo(response.data.partner_info || {});
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        navigate('/banking-partner/login');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${API}/api/banking-partner/leads/${leadId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Lead status updated to ${newStatus}`);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update lead status');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_role');
    navigate('/banking-partner/login');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      in_review: { color: 'bg-yellow-100 text-yellow-800', icon: Eye },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      funded: { color: 'bg-emerald-100 text-emerald-800', icon: DollarSign },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const config = statusConfig[status] || statusConfig.new;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const filteredLeads = leads.filter(lead => {
    const matchesSearch = 
      lead.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.property_address?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Landmark className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{partnerInfo?.bank_name || 'Banking Partner'} Portal</h1>
                <p className="text-sm text-gray-500">Financing Lead Management</p>
              </div>
            </div>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Leads</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_leads}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Loan Value</p>
                  <p className="text-2xl font-bold text-blue-600">{formatCurrency(stats.total_loan_value)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-600">Commission (1%)</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatCurrency(stats.total_commission_owed)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Leads Table */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle>Financing Leads</CardTitle>
                <CardDescription>Leads assigned to your institution</CardDescription>
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border rounded-md text-sm"
                >
                  <option value="all">All Status</option>
                  <option value="new">New</option>
                  <option value="in_review">In Review</option>
                  <option value="approved">Approved</option>
                  <option value="funded">Funded</option>
                  <option value="rejected">Rejected</option>
                </select>
                <Button variant="outline" onClick={fetchDashboardData}>
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredLeads.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No leads found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Client</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Property</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Loan Amount</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Commission (1%)</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Status</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => (
                      <tr key={lead.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900">{lead.full_name}</p>
                            <p className="text-sm text-gray-500">{lead.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <p className="text-sm text-gray-700 max-w-xs truncate">{lead.property_address}</p>
                          <p className="text-xs text-gray-500 capitalize">{lead.financing_type?.replace('_', ' ')}</p>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-semibold text-gray-900">{formatCurrency(lead.loan_amount_requested)}</p>
                          <p className="text-xs text-gray-500">Value: {formatCurrency(lead.estimated_property_value)}</p>
                        </td>
                        <td className="py-3 px-4">
                          <p className="font-semibold text-emerald-600">{formatCurrency(lead.loan_amount_requested * 0.01)}</p>
                        </td>
                        <td className="py-3 px-4">
                          {getStatusBadge(lead.status)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedLead(lead)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {lead.status === 'new' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-yellow-600"
                                onClick={() => handleStatusUpdate(lead.id, 'in_review')}
                              >
                                Review
                              </Button>
                            )}
                            {lead.status === 'in_review' && (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleStatusUpdate(lead.id, 'approved')}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600"
                                  onClick={() => handleStatusUpdate(lead.id, 'rejected')}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                            {lead.status === 'approved' && (
                              <Button
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => handleStatusUpdate(lead.id, 'funded')}
                              >
                                Mark Funded
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Commission Summary */}
        <Card className="mt-6 bg-gradient-to-r from-blue-900 to-indigo-900 text-white">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold">Commission Summary</h3>
                <p className="text-blue-200 text-sm">1% of funded loan amounts</p>
              </div>
              <div className="grid grid-cols-3 gap-8 text-center">
                <div>
                  <p className="text-blue-200 text-sm">Total Commission</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.total_commission_owed)}</p>
                </div>
                <div>
                  <p className="text-blue-200 text-sm">Wellington Woods (50%)</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.total_commission_owed * 0.5)}</p>
                </div>
                <div>
                  <p className="text-blue-200 text-sm">Sebastian Alliance (50%)</p>
                  <p className="text-2xl font-bold">{formatCurrency(stats.total_commission_owed * 0.5)}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Lead Details</CardTitle>
                <Button variant="ghost" onClick={() => setSelectedLead(null)}>✕</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="font-medium">{selectedLead.full_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedLead.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{selectedLead.phone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Company</p>
                  <p className="font-medium">{selectedLead.company_name || 'N/A'}</p>
                </div>
              </div>
              
              <hr />
              
              <div>
                <p className="text-sm text-gray-500">Property Address</p>
                <p className="font-medium">{selectedLead.property_address}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Property Value</p>
                  <p className="font-medium text-blue-600">{formatCurrency(selectedLead.estimated_property_value)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Loan Requested</p>
                  <p className="font-medium text-emerald-600">{formatCurrency(selectedLead.loan_amount_requested)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">LTV Ratio</p>
                  <p className="font-medium">{((selectedLead.loan_amount_requested / selectedLead.estimated_property_value) * 100).toFixed(1)}%</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Business Summary</p>
                <p className="text-sm bg-gray-50 p-3 rounded-lg">{selectedLead.business_summary}</p>
              </div>
              
              {selectedLead.documents && selectedLead.documents.length > 0 && (
                <div>
                  <p className="text-sm text-gray-500 mb-2">Documents</p>
                  <div className="space-y-2">
                    {selectedLead.documents.map((doc, idx) => (
                      <a
                        key={idx}
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:underline"
                      >
                        <Download className="h-4 w-4" />
                        {doc.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default BankingPartnerDashboard;
